import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { useMediaStore } from './media';
import {
  ensureAttachmentArray,
  isAttachmentArray,
} from '@/utils/attachments';
import { emitEvent } from '@/utils/eventBus';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
// Strapi caps page size at 100 by default; stay within that to avoid missing later pages.
const PAGE_SIZE = 100;
const CLIENT_CATALOG_NAME = 'Clients';
const BUILDING_CATALOG_NAME = "Clients’ Buildings";
const SUPPLIER_CATALOG_NAME = 'Suppliers';
const VENDOR_CATALOG_NAME = 'Vendors';
const SERVICE_CATALOG_NAME = 'Services';
const SERVICE_GROUPS_PATH = 'services-groups';
const SERVICE_GROUPS_MAX_AGE_MS = 5 * 60 * 1000;
const GUARDED_RELATION_FIELDS = ['clients', 'buildings', 'services_groups'];

const normalizeCatalogName = (value) => String(value || '').trim().toLowerCase();
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const isServiceCatalogEntry = (catalogId, catalogsById = {}) => {
  if (!catalogId) return false;
  const name = catalogsById?.[catalogId]?.name || '';
  return normalizeCatalogName(name) === normalizeCatalogName(SERVICE_CATALOG_NAME);
};

const normalizeCatalog = (entry) => {
  const attributes = entry?.attributes || entry || {};
  return {
    id: entry?.id ?? attributes.id ?? null,
    documentId: attributes.documentId ?? entry?.documentId ?? null,
    name: attributes.name ?? entry?.name ?? '',
    ...attributes,
  };
};

const normalizeRelationIds = (payload) => {
  if (!payload) return [];
  const data = payload?.data ?? payload;
  if (!Array.isArray(data)) return [];
  return data
    .map((entry) => entry?.id ?? entry?.attributes?.id ?? entry ?? null)
    .filter(Boolean);
};

const normalizeCatalogItem = (entry) => {
  const attributes = entry?.attributes || entry || {};
  const catalogRelation = attributes.catalog?.data || attributes.catalog;
  const catalogId =
    catalogRelation?.id ??
    attributes.catalogId ??
    entry?.catalogId ??
    null;
  const attachmentsRaw = attributes.attachments?.data ?? attributes.attachments ?? [];
  const attachments = ensureAttachmentArray(attachmentsRaw);
  const clients = normalizeRelationIds(attributes.clients);
  const buildings = normalizeRelationIds(attributes.buildings);
  const servicesGroups = normalizeRelationIds(attributes.services_groups);

  return {
    id: entry?.id ?? attributes.id ?? null,
    documentId: attributes.documentId ?? entry?.documentId ?? null,
    catalogId,
    ...attributes,
    archived: Boolean(attributes.archived),
    catalog: undefined,
    attachments,
    clients,
    buildings,
    services_groups: servicesGroups,
  };
};

const normalizeRelationIdsForSave = (payload) => {
  if (!payload) return [];
  const data = payload?.data ?? payload;
  const entries = Array.isArray(data) ? data : [data];
  return entries
    .map((entry) => entry?.id ?? entry?.attributes?.id ?? entry ?? null)
    .filter((value) => value !== null && value !== undefined && value !== '');
};

const preserveMissingCatalogRelations = (model = {}, existingItem = null) => {
  if (!existingItem) return { ...model };
  const next = { ...model };

  GUARDED_RELATION_FIELDS.forEach((field) => {
    if (hasOwn(next, field)) return;
    const sourceValue = existingItem[field];
    if (sourceValue === undefined) return;
    next[field] = normalizeRelationIdsForSave(sourceValue);
  });

  if (!hasOwn(next, 'attachments') && existingItem.attachments !== undefined) {
    next.attachments = ensureAttachmentArray(existingItem.attachments);
  }

  return next;
};

const fetchAllPages = async (path, headers = {}, params = {}) => {
  const results = [];
  let page = 1;
  let pageCount = 1;

  do {
    const response = await axios.get(`${STRAPI_URL}/api/${path}`, {
      headers,
      params: {
        ...params,
        'pagination[page]': page,
        'pagination[pageSize]': params['pagination[pageSize]'] ?? PAGE_SIZE,
      },
    });

    const data = response?.data?.data || [];
    const pagination = response?.data?.meta?.pagination;
    results.push(...data);

    pageCount = pagination?.pageCount || 1;
    page += 1;
  } while (page <= pageCount);

  return results;
};

const sanitizePayload = (data = {}) => {
  const sanitized = {};
  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      sanitized[key] = value;
    } else if (value === '' || value === undefined) {
      sanitized[key] = null;
    } else {
      sanitized[key] = value;
    }
  });
  return sanitized;
};

export const useCatalogStore = defineStore('catalog', {
  state: () => ({
    catalogsById: {},
    catalogIds: [],
    catalogTabIds: [],
    itemsByCatalogId: {},
    activeItemIds: {},
    serviceGroups: [],
    serviceGroupsLoading: false,
    serviceGroupsError: null,
    serviceGroupsStale: false,
    serviceGroupsLastFetchedAt: null,
    loadingCatalogs: false,
    loadingItems: false,
    savingItem: false,
    deletingItem: false,
    error: null,
    itemsError: null,
    activeCatalogId: null,
    initialized: false,
    _bootstrapPromise: null,
  }),
  getters: {
    catalogList(state) {
      return state.catalogIds
        .map((id) => state.catalogsById[id])
        .filter(Boolean);
    },
    catalogTabs(state) {
      return state.catalogTabIds
        .map((id) => state.catalogsById[id])
        .filter(Boolean);
    },
    activeCatalog(state) {
      return state.catalogsById[state.activeCatalogId] || null;
    },
    itemsForActiveCatalog(state) {
      if (!state.activeCatalogId) return [];
      return state.itemsByCatalogId[state.activeCatalogId] || [];
    },
    catalogByName(state) {
      return (name = '') => {
        const target = name.trim().toLowerCase();
        if (!target) return null;
        return state.catalogIds
          .map((id) => state.catalogsById[id])
          .find((catalog) => (catalog?.name || '').toLowerCase() === target) || null;
      };
    },
    optionsByCatalogName() {
      return (name = '') => {
        const catalog = this.catalogByName?.(name);
        if (!catalog?.id) return [];
        const items = this.itemsByCatalogId[catalog.id] || [];
        return items
          .filter((item) => item?.id)
          .map((item) => ({
            id: item.id,
            documentId: item.documentId ?? null,
            name: item.name ?? '',
          }));
      };
    },
    clientOptions() {
      const catalog = this.catalogByName?.(CLIENT_CATALOG_NAME);
      if (!catalog?.id) return [];
      const items = this.itemsByCatalogId[catalog.id] || [];
      return items
        .filter((item) => item?.id)
        .map((item) => {
          const displayName = (item.catalogDisplayName || '').trim();
          return {
            id: item.id,
            documentId: item.documentId ?? null,
            name: displayName || item.name || '',
          };
        });
    },
    buildingOptions() {
      return this.optionsByCatalogName?.(BUILDING_CATALOG_NAME) ?? [];
    },
    supplierOptions() {
      return this.optionsByCatalogName?.(SUPPLIER_CATALOG_NAME) ?? [];
    },
    vendorOptions() {
      return this.optionsByCatalogName?.(VENDOR_CATALOG_NAME) ?? [];
    },
    serviceOptions() {
      return this.optionsByCatalogName?.(SERVICE_CATALOG_NAME) ?? [];
    },
    serviceGroupOptions(state) {
      return (state.serviceGroups || []).map((group) => ({
        id: group.id,
        name: group.name || 'Untitled group',
        services: group.services || [],
      }));
    },
    /**
     * Returns a flat index of all catalog items keyed by their id for O(1) lookups.
     */
    itemsIndexById(state) {
      const index = {};
      Object.values(state.itemsByCatalogId).forEach((items) => {
        items.forEach((item) => {
          if (item?.id) {
            index[item.id] = item;
          }
        });
      });
      return index;
    },
    /**
     * Efficient O(1) lookup for a catalog item by id.
     * @param {string|number} id - The item id to look up.
     * @returns {object|null} The catalog item or null if not found.
     */
    getItemById(state) {
      return (id) => {
        if (!id) return null;
        return this.itemsIndexById[id] ?? null;
      };
    },
  },
  actions: {
    authHeaders() {
      const authStore = useAuthStore();
      return authStore.token ? authStore.requestHeaders.headers : {};
    },

    async bootstrap() {
      if (this._bootstrapPromise) {
        return this._bootstrapPromise;
      }
      if (this.initialized || this.loadingCatalogs) {
        return;
      }

      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        return;
      }

      this._bootstrapPromise = this.loadCatalogsAndItems()
        .catch((error) => {
          this.error = error?.message || 'Failed to load catalogs';
        })
        .finally(() => {
          this._bootstrapPromise = null;
        });

      return this._bootstrapPromise;
    },

    async loadCatalogsAndItems() {
      const headers = this.authHeaders();
      await this.fetchCatalogs(headers);
      this.initialized = true;
      await this.fetchCatalogItems(headers);
      await this.fetchServiceGroups(headers);
    },

    async fetchCatalogs(headers = {}) {
      try {
        this.loadingCatalogs = true;
        this.error = null;

        const entries = await fetchAllPages('catalogs', headers);
        const normalized = entries.map(normalizeCatalog).sort((a, b) => {
          const nameA = a.name?.toLowerCase?.() ?? '';
          const nameB = b.name?.toLowerCase?.() ?? '';
          return nameA.localeCompare(nameB);
        });

        this.catalogIds = normalized.map((catalog) => catalog.id);
        this.catalogsById = normalized.reduce((acc, catalog) => {
          acc[catalog.id] = catalog;
          return acc;
        }, {});

        this.catalogTabIds = this.catalogIds.length ? [this.catalogIds[0]] : [];

        if (!this.activeCatalogId && this.catalogIds.length > 0) {
          this.activeCatalogId = this.catalogIds[0];
        }
      } finally {
        this.loadingCatalogs = false;
      }
    },

    // Reads every catalog's items without touching them, so a page can decide whether it is
    // safe to show what changed elsewhere.
    async peekCatalogItems(headers = this.authHeaders()) {
      const entries = await fetchAllPages('catalog-items', headers, {
        'populate[0]': 'catalog',
        'populate[1]': 'attachments',
        'populate[2]': 'clients',
        'populate[3]': 'buildings',
        'populate[4]': 'services_groups',
      });

      const normalizedItems = entries
        .map(normalizeCatalogItem)
        .filter((item) => item.catalogId && !item.archived);

      const grouped = normalizedItems.reduce((acc, item) => {
        if (!acc[item.catalogId]) {
          acc[item.catalogId] = [];
        }
        acc[item.catalogId].push(item);
        return acc;
      }, {});

      Object.keys(grouped).forEach((catalogId) => {
        grouped[catalogId] = this.sortItems(grouped[catalogId]);
      });

      return grouped;
    },

    async fetchCatalogItems(headers = {}) {
      if (!this.catalogIds.length) return;

      try {
        this.loadingItems = true;
        this.itemsError = null;
        this.itemsByCatalogId = await this.peekCatalogItems(headers);
      } catch (error) {
        this.itemsError = error?.message || 'Failed to load catalog items';
      } finally {
        this.loadingItems = false;
      }
    },

    sortItems(list) {
      return list.sort((a, b) => {
        const rawNameA = a.catalogDisplayName || a.name || '';
        const rawNameB = b.catalogDisplayName || b.name || '';
        const nameA = rawNameA.toLowerCase?.() ?? '';
        const nameB = rawNameB.toLowerCase?.() ?? '';
        return nameA.localeCompare(nameB);
      });
    },

    openCatalogTab(catalogId) {
      if (!this.catalogsById[catalogId]) return;
      if (!this.catalogTabIds.includes(catalogId)) {
        this.catalogTabIds.push(catalogId);
      }
      this.activeCatalogId = catalogId;
    },

    closeCatalogTab(catalogId) {
      const index = this.catalogTabIds.indexOf(catalogId);
      if (index === -1) return;
      this.catalogTabIds.splice(index, 1);
      if (this.activeCatalogId === catalogId) {
        const fallback =
          this.catalogTabIds[index] ??
          this.catalogTabIds[index - 1] ??
          this.catalogIds.find((id) => this.catalogsById[id]) ??
          null;
        this.activeCatalogId = fallback;
      }
    },

    setActiveCatalog(catalogId) {
      this.openCatalogTab(catalogId);
    },

    reset() {
      this.catalogIds = [];
      this.catalogsById = {};
      this.itemsByCatalogId = {};
      this.catalogTabIds = [];
      this.activeItemIds = {};
      this.serviceGroups = [];
      this.serviceGroupsError = null;
      this.serviceGroupsLoading = false;
      this.serviceGroupsStale = false;
      this.serviceGroupsLastFetchedAt = null;
      this.activeCatalogId = null;
      this.initialized = false;
      this.error = null;
      this.itemsError = null;
      this.loadingCatalogs = false;
      this.loadingItems = false;
      this.savingItem = false;
      this.deletingItem = false;
      this._bootstrapPromise = null;
    },

    markServiceGroupsStale() {
      this.serviceGroupsStale = true;
    },

    async ensureServiceGroups({ force = false, maxAgeMs = SERVICE_GROUPS_MAX_AGE_MS } = {}) {
      if (this.serviceGroupsLoading) return;
      const age = this.serviceGroupsLastFetchedAt
        ? Date.now() - this.serviceGroupsLastFetchedAt
        : Infinity;
      const staleByAge = Number.isFinite(maxAgeMs) ? age > maxAgeMs : false;
      if (force || this.serviceGroupsStale || !this.serviceGroups.length || staleByAge) {
        await this.fetchServiceGroups(this.authHeaders());
      }
    },

    setActiveCatalogItemId(catalogId, itemId) {
      if (!catalogId) return;
      if (!itemId) {
        const next = { ...this.activeItemIds };
        delete next[catalogId];
        this.activeItemIds = next;
        return;
      }
      this.activeItemIds = {
        ...this.activeItemIds,
        [catalogId]: itemId,
      };
    },

    async saveCatalogItem(catalogId, model, existingItem = null) {
      if (!catalogId || !model) return null;
      if (this.savingItem) return null;
      const headers = this.authHeaders();
      const normalizedModel = preserveMissingCatalogRelations(
        { ...model, catalog: catalogId },
        existingItem,
      );
      const documentId = existingItem?.documentId || null;
      const attachmentFields = this.resolveAttachmentFields(normalizedModel);
      const mediaStore = useMediaStore();
      const payload = { ...normalizedModel };
      const catalog = this.catalogsById[catalogId];
      if (String(catalog?.key || catalog?.name || '').toLowerCase() === 'services') delete payload.amount;
      delete payload.engagementHistory;
      const deferredDeleteIds = [];
      const uploadedIds = [];
      let saveSucceeded = false;

      try {
        this.savingItem = true;

        for (const field of attachmentFields) {
          const attachments = ensureAttachmentArray(payload[field]);
          const result = await this.persistAttachments(attachments, mediaStore);
          payload[field] = result.ids;
          deferredDeleteIds.push(...result.deferredDeleteIds);
          uploadedIds.push(...result.uploadedIds);
        }

        const endpoint = documentId
          ? `${STRAPI_URL}/api/catalog-items/${documentId}`
          : `${STRAPI_URL}/api/catalog-items`;

        const method = documentId ? 'put' : 'post';
        const response = await axios[method](
          endpoint,
          { data: sanitizePayload(payload) },
          {
            headers,
            params: {
              'populate[0]': 'catalog',
              'populate[1]': 'attachments',
              'populate[2]': 'clients',
              'populate[3]': 'buildings',
              'populate[4]': 'services_groups',
            },
          }
        );
        const saved = normalizeCatalogItem(response?.data?.data || {});
        saved.catalogId = saved.catalogId || catalogId;
        saveSucceeded = true;

        const current = this.itemsByCatalogId[catalogId]
          ? [...this.itemsByCatalogId[catalogId]]
          : [];
        const index = current.findIndex((item) => item.id === saved.id);
        if (index >= 0) {
          current.splice(index, 1, saved);
        } else {
          current.push(saved);
        }
        this.itemsByCatalogId = {
          ...this.itemsByCatalogId,
          [catalogId]: this.sortItems(current),
        };
        this.setActiveCatalogItemId(catalogId, saved.id);
        const catalogName = this.catalogsById[catalogId]?.name ?? null;
        if (isServiceCatalogEntry(catalogId, this.catalogsById)) {
          this.markServiceGroupsStale();
        }
        emitEvent('catalog:changed', { ids: [saved.id], catalogId, catalogName });
        await this.cleanupMediaFiles(deferredDeleteIds, mediaStore);

        return saved;
      } catch (error) {
        if (!saveSucceeded) {
          await this.cleanupMediaFiles(uploadedIds, mediaStore);
        }
        console.error('Failed to save catalog item', error);
        throw error;
      } finally {
        this.savingItem = false;
      }
    },

    async extractRightToWork(itemId, fileId) {
      if (!itemId) throw new Error('Supplier id is required');
      if (!fileId) throw new Error('Attachment file id is required');

      const response = await axios.post(
        `${STRAPI_URL}/api/catalog-items/${itemId}/extract-right-to-work`,
        { fileId },
        { headers: this.authHeaders() },
      );
      return response?.data?.data || null;
    },

    async deleteCatalogItem(catalogId, existingItem) {
      if (!catalogId || !existingItem) return false;
      const documentId = existingItem.documentId || existingItem.id;
      if (!documentId) return false;
      const headers = this.authHeaders();
      const mediaStore = useMediaStore();
      const catalogName = this.catalogsById[catalogId]?.name ?? null;
      try {
        this.deletingItem = true;
        const response = await axios.delete(`${STRAPI_URL}/api/catalog-items/${documentId}`, { headers });
        if (isServiceCatalogEntry(catalogId, this.catalogsById)) {
          this.markServiceGroupsStale();
        }
        const archived = response?.data?.data?.archived ?? response?.data?.archived ?? false;
        if (archived) {
          const current = this.itemsByCatalogId[catalogId] || [];
          const updated = current.filter((item) => item.id !== existingItem.id);
          this.itemsByCatalogId = {
            ...this.itemsByCatalogId,
            [catalogId]: updated,
          };
          if (this.activeItemIds[catalogId] === existingItem.id) {
            this.setActiveCatalogItemId(catalogId, null);
          }
          emitEvent('catalog:changed', { ids: [existingItem.id], catalogId, catalogName, archived: true });
          return true;
        }

        const current = this.itemsByCatalogId[catalogId] || [];
        this.itemsByCatalogId = {
          ...this.itemsByCatalogId,
          [catalogId]: current.filter((item) => item.id !== existingItem.id),
        };
        if (this.activeItemIds[catalogId] === existingItem.id) {
          this.setActiveCatalogItemId(catalogId, null);
        }
        await this.deleteItemAttachments(existingItem, mediaStore);
        emitEvent('catalog:changed', { ids: [existingItem.id], catalogId, catalogName, deleted: true });
        return true;
      } catch (error) {
        console.error('Failed to delete catalog item', error);
        throw error;
      } finally {
        this.deletingItem = false;
      }
    },

    resolveAttachmentFields(model = {}) {
      if (!model) return [];
      return Object.keys(model).filter((key) => isAttachmentArray(model[key]));
    },

    async persistAttachments(attachments = [], mediaStore) {
      if (!attachments?.length) {
        return {
          ids: [],
          deferredDeleteIds: [],
          uploadedIds: [],
        };
      }
      const ids = [];
      const deferredDeleteIds = [];
      const uploadedIds = [];
      for (const attachment of attachments) {
        if (!attachment) continue;
        if (attachment.status === 'delete') {
          if (attachment.id) {
            deferredDeleteIds.push(attachment.id);
          }
          continue;
        }
        if (attachment.status === 'new' && attachment.file) {
          const uploaded = await mediaStore.uploadFile(attachment.file);
          if (uploaded?.id) {
            ids.push(uploaded.id);
            uploadedIds.push(uploaded.id);
          }
          continue;
        }
        if (attachment.id) {
          ids.push(attachment.id);
        }
      }
      return {
        ids: [...new Set(ids)],
        deferredDeleteIds: [...new Set(deferredDeleteIds)],
        uploadedIds: [...new Set(uploadedIds)],
      };
    },

    async cleanupMediaFiles(fileIds = [], mediaStore) {
      const ids = [...new Set((fileIds || []).filter(Boolean))];
      if (!ids.length) return;
      for (const id of ids) {
        try {
          await mediaStore.deleteFile(id);
        } catch (error) {
          console.error(`Failed to cleanup media file ${id}`, error);
        }
      }
    },

    async deleteItemAttachments(item, mediaStore) {
      if (!item) return;
      const fields = this.resolveAttachmentFields(item);
      if (!fields.length) return;
      for (const field of fields) {
        const attachments = ensureAttachmentArray(item[field]);
        for (const attachment of attachments) {
          if (!attachment?.id) continue;
          try {
            await mediaStore.deleteFile(attachment.id);
          } catch (error) {
            console.error(`Failed to delete attachment ${attachment.id}`, error);
          }
        }
      }
    },

    async fetchServiceGroups(headers = {}) {
      try {
        this.serviceGroupsLoading = true;
        this.serviceGroupsError = null;
        const entries = await fetchAllPages(SERVICE_GROUPS_PATH, headers, {
          'populate[0]': 'catalog_items',
        });
        this.serviceGroups = entries.map((entry) => {
          const attributes = entry?.attributes || entry || {};
          const services = normalizeRelationIds(
            attributes.catalog_items,
          );
          return {
            id: entry?.id ?? attributes.id ?? null,
            name: attributes.name ?? entry?.name ?? '',
            services,
          };
        });
        this.serviceGroupsStale = false;
        this.serviceGroupsLastFetchedAt = Date.now();
      } catch (error) {
        this.serviceGroupsError = error?.message || 'Failed to load service groups';
      } finally {
        this.serviceGroupsLoading = false;
      }
    },

  },
});
